import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebRTC } from '../hooks/useWebRTC';
import axios from 'axios';
import { 
  Video, VideoOff, Mic, MicOff, SwitchCamera, 
  Users, X, Radio, Settings, Coins
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '';
const WS_URL = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

export default function BroadcastPage() {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const { token, user, balance } = useAuth();
  
  const [stream, setStream] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [earnings, setEarnings] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const timerRef = useRef(null);
  
  const {
    getLocalStream,
    stopLocalStream,
    createOffer,
    handleAnswer,
    handleIceCandidate,
    closeAllConnections,
    switchCamera,
    toggleMute,
    toggleVideo
  } = useWebRTC();

  // Fetch stream details
  useEffect(() => {
    const fetchStream = async () => {
      try {
        const res = await axios.get(`${API}/streams/${streamId}`);
        setStream(res.data);
      } catch (e) {
        toast.error('Stream not found');
        navigate('/live');
      }
    };
    fetchStream();
  }, [streamId, navigate]);

  // Initialize camera
  useEffect(() => {
    const initCamera = async () => {
      try {
        const localStream = await getLocalStream();
        if (videoRef.current) {
          videoRef.current.srcObject = localStream;
        }
      } catch (e) {
        toast.error('Failed to access camera. Please allow camera permissions.');
      }
    };
    initCamera();
    
    return () => {
      stopLocalStream();
    };
  }, [getLocalStream, stopLocalStream]);

  // Handle WebSocket messages
  const handleWsMessage = useCallback(async (data) => {
    switch (data.type) {
      case 'viewer_connected':
        // Create offer for new viewer
        const offer = await createOffer(data.viewer_id, (viewerId, candidate) => {
          wsRef.current?.send(JSON.stringify({
            type: 'ice_candidate',
            viewer_id: viewerId,
            candidate
          }));
        });
        wsRef.current?.send(JSON.stringify({
          type: 'offer',
          viewer_id: data.viewer_id,
          sdp: offer
        }));
        break;
        
      case 'answer':
        await handleAnswer(data.viewer_id, data.sdp);
        break;
        
      case 'ice_candidate':
        await handleIceCandidate(data.viewer_id, data.candidate);
        break;
        
      case 'viewer_joined':
        setViewerCount(data.viewer_count);
        toast.success('New viewer joined!');
        break;
        
      case 'viewer_left':
        setViewerCount(data.viewer_count);
        break;
        
      default:
        break;
    }
  }, [createOffer, handleAnswer, handleIceCandidate]);

  // Go live
  const goLive = async () => {
    try {
      const wsUrl = `${WS_URL}/ws/broadcast/${streamId}?token=${token}`;
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        setIsLive(true);
        toast.success('You are now live!');
        
        // Start elapsed time counter
        timerRef.current = setInterval(() => {
          setElapsedTime(prev => prev + 1);
        }, 1000);
      };
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        handleWsMessage(data);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        toast.error('Connection error');
      };
      
      wsRef.current.onclose = () => {
        setIsLive(false);
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
      
    } catch (e) {
      console.error('Failed to go live:', e);
      toast.error('Failed to start stream');
    }
  };

  // End stream
  const endStream = async () => {
    try {
      await axios.post(`${API}/streams/${streamId}/end`);
      wsRef.current?.close();
      closeAllConnections();
      toast.success('Stream ended');
      navigate('/live');
    } catch (e) {
      console.error('Failed to end stream:', e);
    }
  };

  // Handle mute toggle
  const handleMuteToggle = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    toggleMute(newMuted);
  };

  // Handle video toggle
  const handleVideoToggle = () => {
    const newVideoOff = !isVideoOff;
    setIsVideoOff(newVideoOff);
    toggleVideo(newVideoOff);
  };

  // Handle camera switch
  const handleSwitchCamera = async () => {
    const newStream = await switchCamera();
    if (newStream && videoRef.current) {
      videoRef.current.srcObject = newStream;
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      wsRef.current?.close();
      closeAllConnections();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [closeAllConnections]);

  if (!stream) {
    return (
      <div className="h-screen w-full bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="h-[100dvh] w-full bg-black flex flex-col relative overflow-hidden" data-testid="broadcast-page">
      <Toaster position="top-center" theme="dark" />
      
      {/* Video container */}
      <div className="flex-1 relative">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
          style={{ transform: 'scaleX(-1)' }}
          data-testid="broadcast-video"
        />
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40 pointer-events-none" />
        
        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-start z-10">
          {/* Live indicator */}
          <div className="flex items-center gap-3">
            {isLive ? (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/90 backdrop-blur-sm rounded-full" data-testid="live-indicator">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span className="text-white font-bold text-sm">LIVE</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800/90 backdrop-blur-sm rounded-full">
                <Radio className="w-4 h-4 text-zinc-400" />
                <span className="text-zinc-400 font-medium text-sm">Preview</span>
              </div>
            )}
            
            {isLive && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full" data-testid="viewer-count">
                <Users className="w-4 h-4 text-white" />
                <span className="text-white font-bold text-sm">{viewerCount}</span>
              </div>
            )}
          </div>
          
          {/* Timer and earnings */}
          <div className="flex flex-col items-end gap-2">
            {isLive && (
              <div className="px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full">
                <span className="text-white font-mono text-sm" data-testid="elapsed-time">{formatTime(elapsedTime)}</span>
              </div>
            )}
            
            <div className="flex items-center gap-2 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full border border-amber-500/20" data-testid="token-display">
              <Coins className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 font-bold text-sm">{stream.tokens_per_minute}/min</span>
            </div>
          </div>
        </div>
        
        {/* Stream title */}
        <div className="absolute bottom-24 left-4 right-4 z-10">
          <h1 className="text-white text-xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {stream.title}
          </h1>
          {stream.description && (
            <p className="text-zinc-400 text-sm">{stream.description}</p>
          )}
        </div>
      </div>
      
      {/* Bottom controls */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
        <div className="flex items-center gap-3 p-2 bg-black/80 backdrop-blur-lg rounded-full border border-white/10">
          {/* Mute button */}
          <button
            onClick={handleMuteToggle}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              isMuted ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white hover:bg-white/20'
            }`}
            data-testid="mute-button"
          >
            {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>
          
          {/* Video toggle */}
          <button
            onClick={handleVideoToggle}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              isVideoOff ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white hover:bg-white/20'
            }`}
            data-testid="video-toggle"
          >
            {isVideoOff ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
          </button>
          
          {/* Switch camera */}
          <button
            onClick={handleSwitchCamera}
            className="w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-all"
            data-testid="switch-camera"
          >
            <SwitchCamera className="w-5 h-5" />
          </button>
          
          {/* Go live / End stream button */}
          {!isLive ? (
            <button
              onClick={goLive}
              className="px-6 py-3 bg-red-500 text-white font-bold rounded-full flex items-center gap-2 hover:bg-red-600 transition-all shadow-lg shadow-red-500/30"
              data-testid="go-live-button"
            >
              <Radio className="w-5 h-5" />
              GO LIVE
            </button>
          ) : (
            <button
              onClick={endStream}
              className="px-6 py-3 bg-zinc-700 text-white font-bold rounded-full flex items-center gap-2 hover:bg-zinc-600 transition-all"
              data-testid="end-stream-button"
            >
              <X className="w-5 h-5" />
              END
            </button>
          )}
        </div>
      </div>
      
      {/* Close button */}
      <button
        onClick={() => {
          if (isLive) {
            if (window.confirm('Are you sure you want to end the stream?')) {
              endStream();
            }
          } else {
            navigate('/live');
          }
        }}
        className="absolute top-4 right-4 z-30 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-all md:hidden"
        data-testid="close-button"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}
